import React, { useEffect, useState } from 'react';
import { api, ApiError, downloadFile } from '../shared/api';
import { formatMoney, formatNumber } from '../shared/formatting';

type Locale = 'hr' | 'en';
type Row = { konto: string; naziv: string | null; n: number; d: number; p: number; nd: number; np: number; saldo: number; saldo_poslije: number; utjecaj: number };
type Sum = { prije: number; poslije: number; utjecaj: number };
type Radna = 'zajednicka' | 'moja_sve';
type Nacrt = { id: number; radna: 'zajednicka' | 'moja'; opis: string; status: string; datum: string; duguje: number; potrazuje: number; stavki: number; uravnotezen: boolean };
type Resp = {
    godina: number; do: string | null; razina: number; rows: Row[];
    totals: { duguje: number; potrazuje: number; nacrt_duguje: number; nacrt_potrazuje: number; razlika_prije: number; razlika_poslije: number };
    sazetak: Record<'aktiva' | 'pasiva' | 'rezultat' | 'razred_8', Sum>; nacrti: Nacrt[];
};

/**
 * 10.1 Probna bilanca: bruto bilanca iz GK do datuma, s utjecajem odabranih
 * nacrta temeljnica, da se ispravak provjeri prije knjizenja u Farosu.
 */
export function GlBilancaTab({ locale, token, company, godina }: { locale: Locale; token: string | null; company: string; godina: number }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [radna, setRadna] = useState<Radna>('zajednicka');
    const [nacrti, setNacrti] = useState<Nacrt[]>([]);
    const [sel, setSel] = useState<number[]>([]);
    const [doDatum, setDo] = useState('');
    const [razina, setRazina] = useState(0);
    const [kOd, setKOd] = useState('');
    const [kDo, setKDo] = useState('');
    const [samoUtjecaj, setSamoUtjecaj] = useState(false);
    const [data, setData] = useState<Resp | null>(null);
    const [ready, setReady] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        api<{ nacrti: Nacrt[] }>(`/gl-analitika/nacrti?company=${company}&godina=${godina}&status=nacrt,odobreno&radna=${radna}`, token)
            .then((r) => { setNacrti(r.nacrti); setSel(r.nacrti.map((n) => n.id)); setReady((x) => x + 1); })
            .catch(() => { setNacrti([]); setSel([]); setReady((x) => x + 1); });
    }, [company, godina, token, radna]);

    const qs = () => {
        const q = new URLSearchParams({ company, godina: String(godina), razina: String(razina), radna });
        if (doDatum) q.set('do', doDatum);
        if (kOd) q.set('konto_od', kOd);
        if (kDo) q.set('konto_do', kDo);
        if (sel.length) q.set('nacrti', sel.join(','));
        return q.toString();
    };

    const load = () => {
        setLoading(true);
        setError(null);
        api<Resp>(`/gl-analitika/bilanca?${qs()}`, token)
            .then(setData)
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
    };

    useEffect(() => { if (ready) load(); }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

    const m = (v: number) => formatMoney(v, locale);
    const num: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap' };
    const rows = (data?.rows ?? []).filter((r) => !samoUtjecaj || Math.abs(r.utjecaj) >= 0.005 || r.nd || r.np);
    const toggle = (id: number) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    const sumLabels: [keyof Resp['sazetak'], string][] = [
        ['aktiva', t('Aktiva (razredi 0, 1, 3, 6)', 'Assets (classes 0, 1, 3, 6)')],
        ['pasiva', t('Pasiva (razredi 2, 9)', 'Liabilities and equity (2, 9)')],
        ['rezultat', t('Rezultat (razredi 4 i 7, P-D)', 'Result (classes 4 and 7, C-D)')],
        ['razred_8', t('Razred 8 (P-D)', 'Class 8 (C-D)')],
    ];

    return (
        <div>
            <div style={{ display: 'flex', gap: 4, marginBottom: '0.6rem' }}>
                <button type="button" className={radna === 'zajednicka' ? 'compact' : 'secondary compact'} onClick={() => setRadna('zajednicka')}>{t('Zajednička radna bilanca', 'Shared working balance')}</button>
                <button type="button" className={radna === 'moja_sve' ? 'compact' : 'secondary compact'} onClick={() => setRadna('moja_sve')}>{t('Moja radna bilanca', 'My working balance')}</button>
            </div>
            <p className="muted" style={{ marginTop: 0 }}>{radna === 'moja_sve'
                ? t('Glavna knjiga + zajednički nacrti + tvoji nacrti. Tvoje nacrte drugi ne vide.', 'General ledger + shared drafts + your drafts. Others do not see your drafts.')
                : t('Glavna knjiga + nacrti iz zajedničke radne bilance.', 'General ledger + drafts from the shared working balance.')}</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.8rem' }}>
                <label>{t('Stanje do', 'As of')}<br /><input type="date" value={doDatum} onChange={(e) => setDo(e.target.value)} /></label>
                <label>{t('Razina', 'Level')}<br />
                    <select value={razina} onChange={(e) => setRazina(Number(e.target.value))}>
                        <option value={0}>{t('Analitička konta', 'Detail accounts')}</option>
                        <option value={1}>{t('Razred (1 znamenka)', 'Class (1 digit)')}</option>
                        <option value={2}>{t('Skupina (2)', 'Group (2)')}</option>
                        <option value={3}>{t('Sintetika (3)', 'Synthetic (3)')}</option>
                        <option value={4}>{t('4 znamenke', '4 digits')}</option>
                    </select>
                </label>
                <label>{t('Konto od', 'Account from')}<br /><input value={kOd} onChange={(e) => setKOd(e.target.value)} style={{ width: 80 }} /></label>
                <label>{t('Konto do', 'Account to')}<br /><input value={kDo} onChange={(e) => setKDo(e.target.value)} style={{ width: 80 }} /></label>
                <label><input type="checkbox" checked={samoUtjecaj} onChange={(e) => setSamoUtjecaj(e.target.checked)} /> {t('samo konta s utjecajem nacrta', 'only accounts affected by drafts')}</label>
                <button type="button" className="compact" onClick={load}>{t('Izračunaj', 'Calculate')}</button>
                <button type="button" className="secondary compact" disabled={!data}
                    onClick={() => downloadFile(`/gl-analitika/bilanca/export.xlsx?${qs()}`, token, `probna-bilanca-${company}-${godina}.xlsx`).catch((e) => setError(e.message))}>
                    {t('Izvoz XLS', 'Export XLS')}
                </button>
                {loading ? <span className="muted">{t('Računam…', 'Calculating…')}</span> : null}
            </div>

            <div style={{ marginBottom: '0.8rem' }}>
                <strong>{t('Nacrti koji ulaze u bilancu', 'Drafts included')}</strong>
                {nacrti.length === 0 ? <span className="muted"> - {t('nema nacrta u statusu nacrt ili odobreno', 'no drafts in draft or approved status')}</span> : null}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 4 }}>
                    {nacrti.map((n) => (
                        <label key={n.id} title={`${n.stavki} ${t('stavki', 'lines')}`}>
                            <input type="checkbox" checked={sel.includes(n.id)} onChange={() => toggle(n.id)} />{' '}
                            #{n.id} {n.radna === 'moja' ? `[${t('moj', 'mine')}] ` : ''}{n.opis} ({n.status === 'odobreno' ? t('odobreno', 'approved') : t('nacrt', 'draft')}, {m(n.duguje)}{n.uravnotezen ? '' : ` ${t('NIJE D=P', 'NOT BALANCED')}`})
                        </label>
                    ))}
                </div>
                {sel.length ? <p className="muted" style={{ margin: '4px 0 0' }}>{t('Promjena odabira traži ponovni izračun.', 'Changing the selection needs a recalculation.')}</p> : null}
            </div>

            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

            {data ? (
                <>
                    <table className="data-table compact" style={{ marginBottom: '1rem', maxWidth: 760 }}>
                        <thead><tr><th></th><th style={num}>{t('Prije', 'Before')}</th><th style={num}>{t('Nakon nacrta', 'After drafts')}</th><th style={num}>{t('Utjecaj', 'Impact')}</th></tr></thead>
                        <tbody>
                            {sumLabels.map(([k, l]) => (
                                <tr key={k}><td>{l}</td><td style={num}>{m(data.sazetak[k].prije)}</td><td style={num}>{m(data.sazetak[k].poslije)}</td><td style={num}>{m(data.sazetak[k].utjecaj)}</td></tr>
                            ))}
                            <tr>
                                <td>{t('Razlika D - P (mora biti 0)', 'Debit - credit (must be 0)')}</td>
                                <td style={{ ...num, fontWeight: Math.abs(data.totals.razlika_prije) >= 0.005 ? 700 : 400 }}>{m(data.totals.razlika_prije)}</td>
                                <td style={{ ...num, fontWeight: Math.abs(data.totals.razlika_poslije) >= 0.005 ? 700 : 400 }}>{m(data.totals.razlika_poslije)}</td>
                                <td style={num}>{m(data.totals.razlika_poslije - data.totals.razlika_prije)}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ overflow: 'auto', maxHeight: 620 }}>
                        <table className="data-table compact">
                            <thead><tr>
                                <th>{t('Konto', 'Account')}</th><th>{t('Naziv', 'Name')}</th><th style={num}>{t('Stavki', 'Lines')}</th>
                                <th style={num}>{t('Duguje', 'Debit')}</th><th style={num}>{t('Potražuje', 'Credit')}</th><th style={num}>{t('Saldo', 'Balance')}</th>
                                <th style={num}>{t('Nacrti D', 'Drafts D')}</th><th style={num}>{t('Nacrti P', 'Drafts C')}</th>
                                <th style={num}>{t('Saldo nakon', 'Balance after')}</th><th style={num}>{t('Utjecaj', 'Impact')}</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.konto} style={Math.abs(r.utjecaj) >= 0.005 ? { fontWeight: 600 } : undefined}>
                                        <td>{r.konto}</td><td>{r.naziv ?? ''}</td><td style={num}>{formatNumber(r.n, locale)}</td>
                                        <td style={num}>{m(r.d)}</td><td style={num}>{m(r.p)}</td><td style={num}>{m(r.saldo)}</td>
                                        <td style={num}>{r.nd ? m(r.nd) : ''}</td><td style={num}>{r.np ? m(r.np) : ''}</td>
                                        <td style={num}>{m(r.saldo_poslije)}</td><td style={num}>{Math.abs(r.utjecaj) >= 0.005 ? m(r.utjecaj) : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot><tr style={{ fontWeight: 700 }}>
                                <td colSpan={3}>{t('Ukupno', 'Total')}</td><td style={num}>{m(data.totals.duguje)}</td><td style={num}>{m(data.totals.potrazuje)}</td>
                                <td style={num}>{m(data.totals.razlika_prije)}</td><td style={num}>{m(data.totals.nacrt_duguje)}</td><td style={num}>{m(data.totals.nacrt_potrazuje)}</td>
                                <td style={num}>{m(data.totals.razlika_poslije)}</td><td></td>
                            </tr></tfoot>
                        </table>
                    </div>
                </>
            ) : null}
        </div>
    );
}
